import { defineStore } from 'pinia'
import { ElementType } from '../models/Element'
import { replaceOrAddElement } from '@/util/tiptap-commands'
import type { CommandProps } from '@/util/tiptap-commands'

export const TABLE_PICKER_MAX_ROWS = 20
export const TABLE_PICKER_MAX_COLUMNS = 10
/** The most a resize grid offers, so a very large table does not render thousands of cells; past it, picking a size
 * shrinks the table, which asks first when content would go */
export const TABLE_PICKER_HARD_MAX_ROWS = 50
export const TABLE_PICKER_HARD_MAX_COLUMNS = 30

/** Slash command props are not stored in Pinia (vue-tsc cannot serialize Editor). */
let pendingSlashProps: CommandProps | null = null
/** Set instead of the slash props when the picker resizes an existing table. */
let pendingTableCallback: ((rows: number, columns: number) => void) | null = null

type TableSlashPickerState = {
  visible: boolean
  hoverRows: number
  hoverColumns: number
  /** The size the picker opens at, and returns to when the pointer leaves the grid */
  startRows: number
  startColumns: number
  /** The grid offered: the usual maximum, or past the size of a larger table being resized */
  gridRows: number
  gridColumns: number
  /** The size of the table being resized (0 for a new table); it may be past the grid */
  currentRows: number
  currentColumns: number
  /** True while the picker resizes an existing table instead of inserting a new one */
  resizing: boolean
}

const NEW_TABLE_SIZE = 3

export type TableSlashPickerStore = ReturnType<typeof useTableSlashPickerStore>

export const useTableSlashPickerStore = defineStore('tableSlashPicker', {
  state: (): TableSlashPickerState => ({
    visible: false,
    hoverRows: NEW_TABLE_SIZE,
    hoverColumns: NEW_TABLE_SIZE,
    startRows: NEW_TABLE_SIZE,
    startColumns: NEW_TABLE_SIZE,
    gridRows: TABLE_PICKER_MAX_ROWS,
    gridColumns: TABLE_PICKER_MAX_COLUMNS,
    currentRows: 0,
    currentColumns: 0,
    resizing: false
  }),
  getters: {
    /**
     * The size a pick means. At the grid's edge, a table larger than the grid keeps that dimension: the edge stands
     * for "as it is", not for removing everything past it.
     */
    displayRows(state): number {
      return state.hoverRows === state.gridRows && state.currentRows > state.gridRows
        ? state.currentRows
        : state.hoverRows
    },
    displayColumns(state): number {
      return state.hoverColumns === state.gridColumns && state.currentColumns > state.gridColumns
        ? state.currentColumns
        : state.hoverColumns
    }
  },
  actions: {
    open(props: CommandProps) {
      pendingSlashProps = props
      pendingTableCallback = null
      this.resizing = false
      this.currentRows = 0
      this.currentColumns = 0
      this.show(NEW_TABLE_SIZE, NEW_TABLE_SIZE, TABLE_PICKER_MAX_ROWS, TABLE_PICKER_MAX_COLUMNS)
    },
    /** Opens at the table's current size, with room to grow past it */
    openForTable(onConfirm: (rows: number, columns: number) => void, current: { rows: number; columns: number }) {
      pendingSlashProps = null
      pendingTableCallback = onConfirm
      this.resizing = true
      this.currentRows = current.rows
      this.currentColumns = current.columns
      const gridRows = Math.min(TABLE_PICKER_HARD_MAX_ROWS, Math.max(TABLE_PICKER_MAX_ROWS, current.rows + 5))
      const gridColumns = Math.min(
        TABLE_PICKER_HARD_MAX_COLUMNS,
        Math.max(TABLE_PICKER_MAX_COLUMNS, current.columns + 2)
      )
      this.show(
        Math.min(Math.max(1, current.rows), gridRows),
        Math.min(Math.max(1, current.columns), gridColumns),
        gridRows,
        gridColumns
      )
    },
    show(rows: number, columns: number, gridRows: number, gridColumns: number) {
      this.gridRows = gridRows
      this.gridColumns = gridColumns
      this.startRows = rows
      this.startColumns = columns
      this.hoverRows = rows
      this.hoverColumns = columns
      this.visible = true
    },
    close() {
      this.visible = false
      pendingSlashProps = null
      pendingTableCallback = null
    },
    /** A table going away closes the picker it opened, so a pick never resizes a table that is gone */
    releaseTable(onConfirm: (rows: number, columns: number) => void) {
      if (pendingTableCallback === onConfirm) this.close()
    },
    setHover(rows: number, columns: number) {
      this.hoverRows = Math.max(1, Math.min(rows, this.gridRows))
      this.hoverColumns = Math.max(1, Math.min(columns, this.gridColumns))
    },
    resetHover() {
      this.hoverRows = this.startRows
      this.hoverColumns = this.startColumns
    },
    confirm() {
      if (pendingTableCallback) {
        const callback = pendingTableCallback
        const { displayRows, displayColumns } = this
        this.close()
        callback(displayRows, displayColumns)
        return
      }
      const props = pendingSlashProps
      this.close()
      if (!props || props.editor.isDestroyed) return
      replaceOrAddElement(props, {
        type: ElementType.table,
        tableRows: this.hoverRows,
        tableColumns: this.hoverColumns
      })
    },
    confirmDefault() {
      // Resizing has no "default size"; the button only applies to inserting a new table
      const props = pendingSlashProps
      if (!props) return
      this.close()
      // The block the slash was typed in may have gone while the picker was open
      if (props.editor.isDestroyed) return
      replaceOrAddElement(props, { type: ElementType.table })
    }
  }
})
